"use client";

import clsx from "clsx";
import { type ComponentProps, useMemo, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { BackgroundMedia } from "@shared/ui/background-media";
import { SwiperNavButtons } from "@shared/ui/slider-nav-buttons";
import { TrailNumber } from "@shared/ui/trail-number";
import { Body, Heading } from "@shared/ui/typography";

import type { UniversalGalleryItemBase } from "./model";

import s from "./universal-gallery.module.scss";

export type UniversalGalleryProps = ComponentProps<"div"> & {
  className?: string;
  /** Optional subtitle displayed above the title */
  subtitle?: string | null;
  /** Main title of the gallery section */
  title?: string | null;
  /** Array of gallery items to display */
  gallery?: UniversalGalleryItemBase[] | null;
};

export const UniversalGallery = (props: UniversalGalleryProps) => {
  const { className, subtitle, title, gallery } = props;

  const [activeIndex, setActiveIndex] = useState(0);
  const $swiper = useRef<SwiperType | null>(null);

  const galleryItems = useMemo((): UniversalGalleryItemBase[] => {
    if (!gallery || gallery.length === 0) return [];
    return gallery;
  }, [gallery]);

  if (!galleryItems.length) return null;

  return (
    <div className={clsx(s.root, className)}>
      <div className={s.top}>
        <div className={s.header}>
          {subtitle && (
            <Body
              className={s.subtitle}
              weight="semiBold"
              size="uppercase-small"
            >
              {subtitle}
            </Body>
          )}
          {title && (
            <Heading level={3} className={s.title}>
              {title}
            </Heading>
          )}
        </div>
        <SwiperNavButtons
          className={s.navButtons}
          variant="dark"
          totalSlides={galleryItems.length}
          currentIndex={activeIndex}
          swiperRef={$swiper}
        />
      </div>
      <Swiper
        modules={[Pagination]}
        pagination={{
          el: `.${s.pagination}`,
          type: "bullets",
          clickable: true,
          bulletClass: `${s.bullet}`,
          bulletActiveClass: `${s.bulletActive}`,
        }}
        slidesPerView="auto"
        className={s.swiper}
        onSlideChange={({ realIndex }) => setActiveIndex(realIndex)}
        onInit={(swiper) => {
          $swiper.current = swiper;
        }}
      >
        {galleryItems.map((item, index) => {
          const { backgroundMedia, title, difficulty, trailNumber, variant } =
            item;
          // Show trail-specific features only for trails variant
          const isTrailsVariant = variant === "trails";

          return (
            <SwiperSlide
              key={`gallery-item-${index}-${title}-${trailNumber || ""}`}
              className={s.slide}
            >
              <BackgroundMedia
                type={backgroundMedia.type}
                image={backgroundMedia.image}
                video={backgroundMedia.video}
                className={s.media}
              />
              <div className={s.slideHeader}>
                {isTrailsVariant && trailNumber && (
                  <TrailNumber size="24" variant={difficulty || "medium"}>
                    {trailNumber}
                  </TrailNumber>
                )}
                <Body size="primary" weight="semiBold" className={s.slideTitle}>
                  {title}
                </Body>
              </div>
            </SwiperSlide>
          );
        })}
        {galleryItems.length > 1 && <div className={s.pagination}></div>}
      </Swiper>
    </div>
  );
};

UniversalGallery.displayName = "UniversalGallery";
